import { PreviewContainer } from 'modules/templateEditor/common';
import { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  borderWidth2,
  caption,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  letterSpacing,
  spacing,
  subtleShadow,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

const windowBackground = `radial-gradient(circle, transparent, rgba(252, 172, 209, 0.2) 100%), radial-gradient(circle, transparent, rgba(181, 109, 198, 0.3) 100%)`;

export const WebPushPreviewContainer = styled(PreviewContainer)`
  /**
   * This needs to be wrapped in styled because it's 
   * used to apply additional styles to PreviewContainer 
   * in other modules.
   */
`;

export const containerStyles: CSSProperties = {
  display: 'flex',
  alignItems: `flex-start`,
  justifyContent: 'center',
  height: `100%`,
};

export const deviceStylesCommon: CSSProperties = {
  display: `flex`,
  flexDirection: `column`,
  alignItems: 'center',
  letterSpacing: letterSpacing.pos10,
  margin: 0,
  overflow: `hidden`,
  fontFamily: fonts.sfPro,
  borderRadius: pxToRem(8),
  width: '100%',
  height: pxToRem(456),
};

export const deviceStylesDesktop: CSSProperties = {
  backgroundColor: colors.purple10,
  backgroundImage: windowBackground,
  borderColor: `transparent`,
};

export const deviceStylesMobile: CSSProperties = {
  backgroundColor: colors.purple10,
  backgroundImage: windowBackground,
  border: `${borderWidth2} ${colors.borderPrimary}`,
  borderRadius: pxToRem(20),
  height: pxToRem(534),
  width: pxToRem(286),
};

export const deviceStylesUnset: CSSProperties = {
  alignItems: `center`,
  backgroundColor: colors.white,
  backgroundImage: 'none',
  borderColor: colors.grey25,
  borderRadius: fontSizes.s18,
  borderStyle: `solid`,
  borderWidth: spacing.eighth,
  justifyContent: `center`,
  textAlign: `center`,
  wordBreak: 'break-word',
  border: `${borderWidth2} ${colors.borderPrimary}`,
};

export const messageUnset: CSSProperties = {
  ...caption,
  color: colors.textSecondary,
  margin: `0 ${spacing.plusHalf}`,
  width: pxToRem(250),
};

export const messageDesktopTime: CSSProperties = {
  paddingRight: pxToRem(10),
};

export const messageDesktopTimeWithPreview: CSSProperties = {
  paddingRight: pxToRem(45),
};

export const messageDesktopBody: CSSProperties = {
  display: `flex`,
  flex: `1 1 auto`,
  flexDirection: `column`,
  justifyContent: `stretch`,
  overflowX: `hidden`,
  overflowY: `auto`,
  width: '100%',
};

export const messageDesktopHeader: CSSProperties = {
  display: `flex`,
  alignItems: `center`,
  backgroundColor: `rgba(255, 255, 255, 0.5)`,
  color: colors.textSecondary,
  columnGap: pxToRem(7),
  fontSize: fontSizes.s12,
  flexDirection: 'row',
  justifyContent: `flex-end`,
  padding: `${pxToRem(6)} 0`,
  width: '100%',
  height: pxToRem(25),
  wordBreak: 'break-word',
};

export const messageDesktopContainer: CSSProperties = {
  backgroundColor: `rgba(255, 255, 255, 0.9)`,
  display: 'grid',
  gridColumnGap: pxToRem(20),
  gridRowGap: pxToRem(12),
  gridTemplateAreas: `'title now' 'content media'`,
  flexDirection: `column`,
  borderRadius: fontSizes.s18,
  fontWeight: fontWeights.regular,
  padding: pxToRem(25),
  margin: `${pxToRem(24)} ${pxToRem(18)}`,
  wordBreak: 'break-word',
  gridTemplateColumns: '1fr 60px',
  ...subtleShadow,
};

export const messageDesktopNow: CSSProperties = {
  gridArea: 'now',
  display: 'flex',
  alignItems: 'center',
  fontFamily: pxToRem(18),
  fontSize: fontSizes.s18,
  justifyContent: 'end',
  lineHeight: spacing.base,
  fontWeight: fontWeights.light,
  wordBreak: 'break-word',
};

export const messageDesktopTitle: CSSProperties = {
  gridArea: 'title',
  fontFamily: fonts.sfPro,
  fontWeight: fontWeights.semiBold,
  fontSize: fontSizes.s20,
  lineHeight: spacing.base,
  wordBreak: 'break-word',
  whiteSpace: 'break-spaces',
  paddingBottom: '0',
};

export const messageDesktopContent: CSSProperties = {
  display: 'flex',
  gridArea: 'content',
  fontFamily: fonts.sfPro,
  fontSize: fontSizes.s18,
  fontWeight: fontWeights.light,
  lineHeight: pxToRem(21),
  flexDirection: 'column',
  columnGap: spacing.threeEights,
  wordBreak: 'break-word',
  paddingTop: '0',
};

export const messageDesktopMedia: CSSProperties = {
  display: 'flex',
  gridArea: 'media',
  alignSelf: 'center',
  backgroundSize: `cover`,
  height: pxToRem(54),
  width: pxToRem(54),
  justifySelf: 'end',
  borderRadius: spacing.threeEights,
};

export const mobileTimeStyle: CSSProperties = {
  color: colors.white,
  fontSize: pxToRem(70),
  letterSpacing: letterSpacing.pos10,
  lineHeight: 1,
  textAlign: `center`,
  textShadow: `
    0 0 ${pxToRem(1)} rgba(0, 0, 0, 0.04),
    0 0 ${pxToRem(2)} rgba(0, 0, 0, 0.06),
    ${pxToRem(1)} ${pxToRem(1)} ${pxToRem(7)} rgba(0, 0, 0, 0.15)
  `,
  fontWeight: fontWeights.light,
  marginTop: pxToRem(60),
};

export const mobileDateStyle: CSSProperties = {
  color: colors.white,
  fontSize: pxToRem(18),
  fontWeight: fontWeights.regular,
  letterSpacing: letterSpacing.pos10,
  textAlign: `center`,
  textShadow: `
    0 0 ${pxToRem(1)} rgba(0, 0, 0, 0.04),
    0 0 ${pxToRem(2)} rgba(0, 0, 0, 0.06),
    0 ${pxToRem(1)} ${pxToRem(3)} rgba(0, 0, 0, 0.15)
  `,
};

export const mobileNotificationBubble: CSSProperties = {
  backgroundColor: `rgba(255, 255, 255, 0.7)`,
  borderRadius: pxToRem(9),
  boxSizing: `border-box`,
  display: `flex`,
  marginTop: pxToRem(16),
  padding: `${pxToRem(8)} ${pxToRem(11)} ${pxToRem(7)} ${pxToRem(6)}`,
  width: pxToRem(278),
};

export const mobileNotificationPlaceholder: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
};

export const mobileNotificationContent: CSSProperties = {
  paddingLeft: spacing.half,
  width: pxToRem(230),
};

export const mobileNotificationContentHeader: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  width: `inherit`,
};

export const mobileNotificationContentHeaderSender: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  fontWeight: fontWeights.semiBold,
  overflow: `hidden`,
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
};

export const mobileNotificationContentHeaderTime: CSSProperties = {
  color: colors.textSecondary,
  flex: `0 0 auto`,
  fontSize: pxToRem(10),
  marginLeft: pxToRem(5),
};

export const mobileNotificationContentWrapper: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
};

export const mobileNotificationContentBody: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  width: pxToRem(190),
};

export const mobileNotificationContentBodyMsg: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  overflow: `hidden`,
  overflowWrap: `break-word`,
};

export const mobileNotificationAttachmentWrapper: CSSProperties = {
  height: pxToRem(28),
  width: pxToRem(28),
};

export const mobileNotificationAttachmentIos: CSSProperties = {
  height: pxToRem(28),
  width: pxToRem(28),
  borderRadius: pxToRem(4),
};
